import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { FileBarChart2, CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { PLAYER_GROUPS } from "@/lib/navigation";
import { toast } from "sonner";
import { PainAlerts } from "@/components/PainAlerts";
import { ShareTwinButton } from "@/components/ShareCard";

export default function Dashboard() {
  const { user, can } = useAuth();
  const { t, lang } = useLang();
  const [avatars, setAvatars] = useState([]);
  const [stats, setStats] = useState([]);
  const [report, setReport] = useState(null);
  const [daily, setDaily] = useState([]);

  useEffect(() => {
    api.get("/avatar").then((r) => setAvatars(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/daily").then((r) => setDaily(r.data)).catch(() => {});
    api.get("/reports/weekly").then(async (r) => {
      const cur = r.data.items.find((i) => i.week_key === r.data.current_week_key);
      if (cur) return setReport(cur);
      const day = new Date().getDay();
      if ((day !== 0 && day !== 1) || !can("report")) return;
      // Auto-generate on the first visit of Sunday / Monday
      try {
        const gen = await api.post("/reports/weekly/generate", { lang, auto: true });
        setReport(gen.data);
        toast.success(lang === "fr" ? "Ton rapport hebdo est prêt" : "Your weekly report is ready");
      } catch { /* not enough data yet */ }
    }).catch(() => {});
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps


  const latest = stats[0] || {};

  const fr = lang === "fr";
  const steps = [
    { done: avatars.length > 0, to: "/avatar", title: fr ? "Crée ton jumeau IA" : "Create your AI twin", desc: fr ? "Une photo suffit, 30 secondes." : "One photo is enough, 30 seconds." },
    { done: daily.length > 0, to: "/daily", title: fr ? "Note ta première séance" : "Log your first session", desc: fr ? "Humeur, énergie, douleurs — 2 minutes." : "Mood, energy, pain — 2 minutes." },
    { done: stats.length > 0, to: "/stats", title: fr ? "Entre tes stats" : "Enter your stats", desc: fr ? "Poids, vitesse, VO2 pour suivre ta progression." : "Weight, speed, VO2 to track progress." },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="dashboard-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">DLSPORT · CONTROL DECK</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="dashboard-title">
        {t.dashboard.welcome}, <span className="text-[#00F076]">{user?.full_name?.split(" ")[0] || "Player"}</span>
      </h1>
      <div className="mt-2 flex flex-wrap items-center gap-4">
        <div className="text-slate-400 text-sm">{user?.position}</div>
        {can("share") && <ShareTwinButton size="sm" avatarSrc={avatars[0]?.image_data_url} name={user?.full_name} position={user?.position} scores={report?.report?.scores} stat={latest} weekKey={report?.week_key} />}
      </div>

      <div className="mt-6"><PainAlerts /></div>

      {steps.some((st) => !st.done) && (
        <div className="mt-8 rounded-xl border border-[#00F076]/30 bg-[#00F076]/5 p-5" data-testid="dashboard-getting-started">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-display font-bold uppercase text-lg">{fr ? "Bien démarrer en 3 étapes" : "Get started in 3 steps"}</div>
            <div className="text-xs font-mono-tech text-[#00F076]">{steps.filter((st) => st.done).length}/3</div>
          </div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            {steps.map((st, i) => (
              <Link key={i} to={st.to} data-testid={`getting-started-${i}`} className={`rounded-lg border p-4 flex gap-3 transition-colors ${st.done ? "border-[#1E2638] bg-[#090C10] opacity-60" : "border-[#00F076]/40 bg-[#121721] hover:border-[#00F076]"}`}>
                {st.done ? <CheckCircle2 className="w-5 h-5 text-[#00F076] shrink-0" /> : <Circle className="w-5 h-5 text-slate-500 shrink-0" />}
                <div><div className="text-sm font-bold">{i + 1}. {st.title}</div><div className="text-xs text-slate-400 mt-0.5">{st.desc}</div></div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="text-[10px] font-mono-tech tracking-[0.25em] text-slate-500 uppercase mb-3">{fr ? "Que veux-tu faire ?" : "What do you want to do?"}</div>
        <div className="grid md:grid-cols-3 gap-4">
          {PLAYER_GROUPS.filter((g) => g.items.length).map((g) => (
            <div key={g.id} data-testid={`dashboard-section-${g.id}`} className="rounded-xl border border-[#1E2638] bg-[#121721] p-5">
              <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-lg bg-[#00F076]/10 border border-[#00F076]/30 flex items-center justify-center"><g.icon className="w-5 h-5 text-[#00F076]" /></div>
                <div><div className="font-display font-bold text-lg uppercase leading-tight">{fr ? g.fr : g.en}</div><div className="text-[11px] text-slate-400">{fr ? g.frDesc : g.enDesc}</div></div></div>
              <div className="mt-4 space-y-1">
                {g.items.map((it) => (
                  <Link key={it.to} to={it.to} data-testid={`dashboard-link-${it.to.slice(1)}`} className="group flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-[#00F076]/10 transition-colors">
                    <it.icon className="w-4 h-4 text-[#00F076] shrink-0" />
                    <div className="flex-1 min-w-0"><div className="text-sm font-semibold">{fr ? it.fr : it.en}</div><div className="text-[11px] text-slate-500 truncate">{fr ? it.frDesc : it.enDesc}</div></div>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-[#00F076] group-hover:translate-x-0.5 transition-all" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12 grid lg:grid-cols-3 gap-6">
        <Link to="/report" data-testid="dashboard-report-card" className={`lg:col-span-3 rounded-xl border p-6 flex flex-wrap items-center gap-4 transition-colors ${report ? "border-[#00F076]/50 bg-[#00F076]/5 hover:bg-[#00F076]/10" : "border-[#1E2638] bg-[#121721] hover:border-[#00F076]/40"}`}>
          <div className="w-10 h-10 rounded-lg bg-[#00F076]/10 border border-[#00F076]/30 flex items-center justify-center shrink-0"><FileBarChart2 className="w-5 h-5 text-[#00F076]" /></div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] font-mono-tech tracking-[0.25em] text-emerald-400 uppercase">{t.nav.report} · {report?.week_key || (lang === "fr" ? "cette semaine" : "this week")}</div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">
              {report?.report?.headline || (lang === "fr" ? "Génère ton rapport IA hebdo →" : "Generate your weekly AI report →")}
            </div>
          </div>
          {report?.report?.scores && (
            <div className="flex gap-3 font-mono-tech text-xs">
              {["readiness", "injury_risk"].filter((k) => report.report.scores[k] != null).map((k) => (
                <div key={k} className="text-center"><div className="text-slate-400 uppercase text-[10px]">{k.replace("_", " ")}</div><div className={`font-display font-black text-xl ${k === "injury_risk" ? "text-[#FF2E54]" : "text-[#00F076]"}`}>{report.report.scores[k]}</div></div>
              ))}
            </div>
          )}
        </Link>
        <div className="lg:col-span-2 rounded-xl border border-[#1E2638] bg-[#121721] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-xl uppercase">{t.dashboard.recentAvatars}</div>
            <Link to="/avatar" className="text-xs font-mono-tech tracking-wider text-emerald-400 hover:text-white">+ NEW</Link>
          </div>
          {avatars.length === 0 ? (
            <div className="text-slate-500 text-sm py-10 text-center" data-testid="dashboard-empty">{t.dashboard.empty}</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {avatars.slice(0, 6).map((a) => (
                <div key={a.id} className="relative rounded-lg overflow-hidden border border-[#1E2638] aspect-square">
                  <img src={a.image_data_url} alt={a.variant} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-[#090C10]/80 text-[10px] font-mono-tech tracking-wider uppercase">{a.variant}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
          <div className="font-display font-bold text-xl uppercase mb-4">{lang === "fr" ? "Dernières mesures" : "Latest Metrics"}</div>
          <div className="space-y-3 text-sm">
            {[
              [lang === "fr" ? "Poids" : "Weight", latest.weight_kg, "kg"],
              [lang === "fr" ? "Taille" : "Height", latest.height_cm, "cm"],
              [lang === "fr" ? "Vitesse max" : "Top Speed", latest.top_speed_kmh, "km/h"],
              ["VO2 Max", latest.endurance_vo2, ""],
              ["Muscle", latest.muscle_index, ""],
              [lang === "fr" ? "Puissance" : "Power", latest.explosive_power, ""],
            ].map(([k, v, unit]) => (
              <div key={k} className="flex justify-between border-b border-[#1E2638] pb-2">
                <span className="text-slate-400 font-mono-tech uppercase text-xs tracking-wider">{k}</span>
                <span className="font-display font-bold">{v ?? "—"} <span className="text-slate-500 text-xs">{unit}</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
